// §5.2 — single openapi-fetch instance. Cookie-based session, CSRF + 401-retry middleware.
// Domain wrappers live in `src/features/<name>/api.ts`; pages do not import this.
// Uses the in-house minimal client (src/lib/api/minimal-fetch-client.ts) that replaces
// openapi-fetch to save ~4 kB gzip in the production bundle.
import createClient from "./minimal-fetch-client";
import type { paths } from "@generated/api/schema";
import { withCsrf } from "./csrf";
import { withRenewRetry } from "./middleware";

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v2";
const DUPLICATED_API_PREFIX_RE = /\/api\/v2\/api\/v2(?=\/|$)/g;

function normalizeApiRequestUrl(url: string): string {
  return url.replace(DUPLICATED_API_PREFIX_RE, "/api/v2");
}

function normalizeApiRequest(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof input === "string") {
    return normalizeApiRequestUrl(input);
  }
  if (input instanceof URL) {
    const normalized = normalizeApiRequestUrl(input.href);
    return normalized === input.href ? input : new URL(normalized);
  }
  const normalized = normalizeApiRequestUrl(input.url);
  return normalized === input.url ? input : new Request(normalized, input);
}

// Chain: base fetch → CSRF headers → 401 renew-retry.
const baseFetch: typeof fetch = (input, init) =>
  globalThis.fetch(normalizeApiRequest(input), init);

export const apiClient = createClient<paths>({
  baseUrl,
  credentials: "include",
  fetch: withRenewRetry(withCsrf(baseFetch)),
});

export type ApiPaths = paths;
